/**
 * About / Marketing Page
 *
 * Public marketing page showcasing game features, updates, roadmap,
 * and community information.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageAtmosphere } from '../components/atmosphere';
import { NineSliceBorder } from '../components/atmosphere/NineSliceBorder';
import styles from './AboutPage.module.css';

function OrnateDivider() {
  return (
    <div className={styles.ornateDivider}>
      <img
        src="/assets/UI/pixel-borders/divider-ornate.png"
        alt=""
        className={styles.ornateDividerImg}
      />
    </div>
  );
}

// Assets from public folder - all clean screenshots without console logs
const logoFull = '/assets/branding/logo-full.jpeg';
const battleScreenshot = '/assets/marketing/screenshots/vizenhancements2.png';
const vizenhancements = '/assets/marketing/screenshots/vizenhancements.png';
const damageViz = '/assets/marketing/screenshots/damageviz.png';
const damageTicks = '/assets/marketing/screenshots/damageticks.png';
const creatureDetailFire = '/assets/marketing/screenshots/drawingcreaturebattleview.png';
const creatureDetailBat = '/assets/marketing/screenshots/batgen.png';

// GIFs
const fireBreathGif = '/assets/marketing/gifs/breathe_fire.gif';
const fireSpellGif = '/assets/marketing/gifs/cast_fire_spell.gif';
const lightningGif = '/assets/marketing/gifs/lightning_strike_effect.gif';
const swordGif = '/assets/marketing/gifs/attack_melee_sword.gif';

// Roadmap data - Alpha is active, Beta/Launch TBD based on community feedback
const ROADMAP_ITEMS = [
  {
    phase: 'Alpha',
    status: 'current',
    title: 'Core Experience',
    items: [
      'AI creature generation from drawings & text',
      'Real-time tactical hex-grid combat',
      'Creature gallery & collection management',
      'Basic matchmaking & battle lobbies',
      'Combat animations & visual effects',
    ],
  },
  {
    phase: 'Beta',
    status: 'upcoming',
    title: 'Coming Soon',
    items: [
      'Features shaped by alpha tester feedback',
      'Your suggestions drive what comes next',
      'Join Discord to vote on priorities',
    ],
  },
  {
    phase: 'Launch',
    status: 'planned',
    title: 'The Future',
    items: [
      'Roadmap defined by our community',
      'Built together with our players',
    ],
  },
];

// SVG icons for features (line art style, uses currentColor)
const FEATURE_ICONS: Record<string, React.ReactNode> = {
  pencil: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
      <path d="M17 3a2.85 2.85 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  ),
  cpu: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
      <rect x="4" y="4" width="16" height="16" />
      <rect x="9" y="9" width="6" height="6" />
      <line x1="9" y1="1" x2="9" y2="4" /><line x1="15" y1="1" x2="15" y2="4" />
      <line x1="9" y1="20" x2="9" y2="23" /><line x1="15" y1="20" x2="15" y2="23" />
      <line x1="20" y1="9" x2="23" y2="9" /><line x1="20" y1="14" x2="23" y2="14" />
      <line x1="1" y1="9" x2="4" y2="9" /><line x1="1" y1="14" x2="4" y2="14" />
    </svg>
  ),
  swords: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
      <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
      <line x1="13" y1="19" x2="19" y2="13" />
      <line x1="16" y1="16" x2="20" y2="20" />
      <line x1="19" y1="21" x2="21" y2="19" />
      <polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5" />
      <line x1="5" y1="14" x2="9" y2="18" />
      <line x1="7" y1="17" x2="4" y2="20" />
      <line x1="3" y1="19" x2="5" y2="21" />
    </svg>
  ),
  gamepad: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
      <line x1="6" y1="12" x2="10" y2="12" /><line x1="8" y1="10" x2="8" y2="14" />
      <line x1="15" y1="13" x2="15.01" y2="13" /><line x1="18" y1="11" x2="18.01" y2="11" />
      <rect x="2" y="6" width="20" height="12" rx="2" />
    </svg>
  ),
  library: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
    </svg>
  ),
  rocket: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
  ),
  trophy: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
  ),
};

// Core features
const FEATURES = [
  {
    icon: 'pencil',
    title: 'Draw Anything',
    description: 'Sketch your creature or describe it with text. Dragons, robots, magical beasts - if you can imagine it, you can create it.',
    color: 'amber',
    image: creatureDetailFire,
  },
  {
    icon: 'cpu',
    title: 'AI Generation',
    description: 'Our AI analyzes your creation and generates fully animated pixel art sprites with unique combat abilities based on your design.',
    color: 'purple',
    image: creatureDetailBat,
  },
  {
    icon: 'swords',
    title: 'Tactical Combat',
    description: 'Deploy your creatures on hex-grid battlefields. Position matters - use terrain and abilities strategically to defeat opponents.',
    color: 'cyan',
    image: battleScreenshot,
  },
  {
    icon: 'gamepad',
    title: 'Real-Time Battles',
    description: 'Watch your creatures fight in real-time with smooth 60 FPS animations. Every attack, spell, and movement is visualized.',
    color: 'amber',
    image: damageViz,
  },
  {
    icon: 'library',
    title: 'Build Your Army',
    description: 'Collect unique creatures in your gallery. Each one has procedurally generated stats and abilities ready for battle.',
    color: 'purple',
    image: damageTicks,
  },
  {
    icon: 'rocket',
    title: 'Rapid Development',
    description: 'Built with AI-assisted "vibe coding" - features ship fast. Suggest something on Discord and see it in-game within days, not months.',
    color: 'cyan',
    image: null,
  },
  {
    icon: 'trophy',
    title: 'Shape the Game',
    description: 'As an alpha tester, your feedback directly influences development. This is your chance to help create the game you want to play.',
    color: 'amber',
    image: vizenhancements,
  },
];

export function AboutPage() {
  const [activeTab, setActiveTab] = useState<'features' | 'media' | 'roadmap'>('features');

  return (
    <PageAtmosphere page="about">
    <div className={styles.container}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <img src={logoFull} alt="Drawn of War" className={styles.heroLogo} />
          <h1 className={styles.heroTitle}>Draw. Generate. Battle.</h1>
          <p className={styles.heroSubtitle}>
            Create creatures from your imagination. Watch AI bring them to life. Battle your friends.
          </p>
          <div className={styles.heroCta}>
            <Link to="/welcome" className={styles.primaryButton}>
              Join the Alpha
            </Link>
            <a
              href="https://discord.gg/YFTVtMVG4s"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.secondaryButton}
            >
              Join Discord
            </a>
          </div>
        </div>

        {/* Hero Screenshot */}
        <div className={styles.heroImage}>
          <img src={battleScreenshot} alt="Battle in progress" />
        </div>

        {/* Gameplay Video */}
        <div className={styles.videoSection}>
          <div className={styles.videoContainer}>
            <iframe
              src="https://www.youtube.com/embed/rFEFz0aj1B0"
              title="Drawn of War Gameplay"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      <OrnateDivider />

      {/* Alpha Callout - Fast Iteration */}
      <section className={styles.alphaCallout}>
        <div className={styles.calloutContent}>
          <span className={styles.calloutBadge}>ALPHA ADVANTAGE</span>
          <h2 className={styles.calloutTitle}>Your Ideas, Shipped Fast</h2>
          <p className={styles.calloutText}>
            Drawn of War is built with AI-assisted development - we call it "vibe coding."
            This means features can go from suggestion to live in days, not months.
            Alpha testers aren't just players - you're co-creators shaping the game in real-time.
          </p>
          <div className={styles.calloutStats}>
            <div className={styles.stat}>
              <span className={styles.statNumber}>Days</span>
              <span className={styles.statLabel}>Not months for new features</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statNumber}>Direct</span>
              <span className={styles.statLabel}>Discord access to the dev</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statNumber}>Real</span>
              <span className={styles.statLabel}>Impact on the roadmap</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className={styles.howItWorks}>
        <h2 className={styles.sectionTitle}>How It Works</h2>
        <div className={styles.stepsGrid}>
          <div className={styles.step}>
            <NineSliceBorder
              cornerSrc="/assets/UI/pixel-borders/9s-dark-corner-tl.png"
              edgeTopSrc="/assets/UI/pixel-borders/9s-dark-edge-top.png"
              edgeLeftSrc="/assets/UI/pixel-borders/9s-dark-edge-left.png"
            />
            <div className={styles.stepNumber}>1</div>
            <h3>Create</h3>
            <p>Draw a sketch or describe your creature idea</p>
          </div>
          <div className={styles.stepArrow}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </div>
          <div className={styles.step}>
            <NineSliceBorder
              cornerSrc="/assets/UI/pixel-borders/9s-dark-corner-tl.png"
              edgeTopSrc="/assets/UI/pixel-borders/9s-dark-edge-top.png"
              edgeLeftSrc="/assets/UI/pixel-borders/9s-dark-edge-left.png"
            />
            <div className={styles.stepNumber}>2</div>
            <h3>Generate</h3>
            <p>AI creates animated pixel art with abilities</p>
          </div>
          <div className={styles.stepArrow}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </div>
          <div className={styles.step}>
            <NineSliceBorder
              cornerSrc="/assets/UI/pixel-borders/9s-dark-corner-tl.png"
              edgeTopSrc="/assets/UI/pixel-borders/9s-dark-edge-top.png"
              edgeLeftSrc="/assets/UI/pixel-borders/9s-dark-edge-left.png"
            />
            <div className={styles.stepNumber}>3</div>
            <h3>Battle</h3>
            <p>Deploy your army and fight other players</p>
          </div>
        </div>
      </section>

      {/* Tab Navigation - Prominent */}
      <nav className={styles.tabNav}>
        <h2 className={styles.tabSectionTitle}>Explore the Game</h2>
        <div className={styles.tabButtons}>
          <button
            className={`${styles.tabButton} ${activeTab === 'features' ? styles.active : ''}`}
            onClick={() => setActiveTab('features')}
          >
            <span className={styles.tabIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
            </span>
            <span className={styles.tabLabel}>Features</span>
            <span className={styles.tabDesc}>What you can do</span>
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'media' ? styles.active : ''}`}
            onClick={() => setActiveTab('media')}
          >
            <span className={styles.tabIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            </span>
            <span className={styles.tabLabel}>Media</span>
            <span className={styles.tabDesc}>See it in action</span>
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'roadmap' ? styles.active : ''}`}
            onClick={() => setActiveTab('roadmap')}
          >
            <span className={styles.tabIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
                <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
                <line x1="9" y1="3" x2="9" y2="18" /><line x1="15" y1="6" x2="15" y2="21" />
              </svg>
            </span>
            <span className={styles.tabLabel}>Roadmap</span>
            <span className={styles.tabDesc}>What's coming</span>
          </button>
        </div>
      </nav>

      {/* Features Tab */}
      {activeTab === 'features' && (
        <section className={styles.features}>
          <div className={styles.featuresGrid}>
            {FEATURES.map((feature, index) => (
              <div key={index} className={`${styles.featureCard} ${styles[feature.color]}`}>
                <NineSliceBorder
                  cornerSrc="/assets/UI/pixel-borders/9s-dark-corner-tl.png"
                  edgeTopSrc="/assets/UI/pixel-borders/9s-dark-edge-top.png"
                  edgeLeftSrc="/assets/UI/pixel-borders/9s-dark-edge-left.png"
                />
                <span className={styles.featureIcon}>{FEATURE_ICONS[feature.icon]}</span>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <p className={styles.featureDescription}>{feature.description}</p>
                {feature.image && (
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className={styles.featureImage}
                  />
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Media Gallery Tab */}
      {activeTab === 'media' && (
        <section className={styles.mediaGallery}>
          <h3 className={styles.galleryTitle}>Combat Effects</h3>
          <div className={styles.gifGrid}>
            <div className={styles.gifCard}>
              <img src={fireBreathGif} alt="Fire Breath" />
              <span>Fire Breath</span>
            </div>
            <div className={styles.gifCard}>
              <img src={fireSpellGif} alt="Fire Spell" />
              <span>Fire Spell</span>
            </div>
            <div className={styles.gifCard}>
              <img src={lightningGif} alt="Lightning Strike" />
              <span>Lightning Strike</span>
            </div>
            <div className={styles.gifCard}>
              <img src={swordGif} alt="Sword Attack" />
              <span>Melee Attack</span>
            </div>
          </div>

          <h3 className={styles.galleryTitle}>Screenshots</h3>
          <div className={styles.screenshotGrid}>
            <div className={styles.screenshotCard}>
              <img src={battleScreenshot} alt="Battle scene with multiple creatures" />
              <span>Epic Battles</span>
            </div>
            <div className={styles.screenshotCard}>
              <img src={damageViz} alt="Combat abilities in action" />
              <span>Combat Abilities</span>
            </div>
            <div className={styles.screenshotCard}>
              <img src={vizenhancements} alt="Battle with health bars" />
              <span>Hex-Grid Battlefields</span>
            </div>
            <div className={styles.screenshotCard}>
              <img src={damageTicks} alt="Combat in progress" />
              <span>Real-Time Combat</span>
            </div>
            <div className={styles.screenshotCard}>
              <img src={creatureDetailFire} alt="Fire creature with abilities" />
              <span>Creature Details</span>
            </div>
            <div className={styles.screenshotCard}>
              <img src={creatureDetailBat} alt="Bat creature with abilities" />
              <span>AI-Generated Creatures</span>
            </div>
          </div>
        </section>
      )}

      {/* Roadmap Tab */}
      {activeTab === 'roadmap' && (
        <section className={styles.roadmap}>
          <div className={styles.roadmapTimeline}>
            {ROADMAP_ITEMS.map((phase, index) => (
              <div key={index} className={`${styles.roadmapPhase} ${styles[phase.status]}`}>
                <div className={styles.phaseMarker}>
                  {phase.status === 'current' && <span className={styles.currentBadge}>NOW</span>}
                </div>
                <div className={styles.phaseContent}>
                  <h3 className={styles.phaseTitle}>
                    <span className={styles.phaseName}>{phase.phase}</span>
                    <span className={styles.phaseSubtitle}>{phase.title}</span>
                  </h3>
                  <ul className={styles.phaseItems}>
                    {phase.items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          <p className={styles.roadmapNote}>
            Roadmap priorities are shaped by community feedback.
            <Link to="/welcome"> Join the alpha</Link> to help guide development!
          </p>
        </section>
      )}

      <OrnateDivider />

      {/* Community Section */}
      <section className={styles.community}>
        <h2 className={styles.sectionTitle}>Join the Community</h2>
        <p className={styles.communityText}>
          Drawn of War is being built together with our players. Join our Discord to:
        </p>
        <ul className={styles.communityBenefits}>
          <li>Get alpha access and test new features first</li>
          <li>Share your creature creations</li>
          <li>Give feedback directly to the dev team</li>
          <li>Find opponents and make friends</li>
          <li>Participate in community battles</li>
        </ul>
        <a
          href="https://discord.gg/YFTVtMVG4s"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.discordButton}
        >
          <svg className={styles.discordIcon} viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
          </svg>
          Join Discord Server
        </a>
      </section>

      {/* Final CTA */}
      <section className={styles.finalCta}>
        <h2 className={styles.ctaTitle}>Ready to Create Your Army?</h2>
        <p className={styles.ctaText}>
          Alpha access is open. Request your spot and start creating today.
        </p>
        <Link to="/welcome" className={styles.primaryButton}>
          Request Alpha Access
        </Link>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerLinks}>
          <Link to="/welcome">Join Alpha</Link>
          <Link to="/about">About</Link>
          <a href="https://discord.gg/YFTVtMVG4s" target="_blank" rel="noopener noreferrer">Discord</a>
          <Link to="/login">Sign In</Link>
        </div>
        <p className={styles.copyright}>© 2026 Drawn of War. All rights reserved.</p>
      </footer>
    </div>
    </PageAtmosphere>
  );
}
